// utils/addressResolver.js
// นำเข้าไฟล์ JSON ของคุณ (วางไว้ใน src/assets หรือที่ใดก็ได้ที่บันเดิลได้)
import provinces from '@/assets/json/provinces.json';
import districts from '@/assets/json/districts.json';
import subdistricts from '@/assets/json/subdistricts.json';

// สร้างดัชนี (Map) เพื่อให้ค้นหา O(1)
const provinceByCode = new Map(provinces.map(p => [Number(p.provinceCode), p]));
const districtByCode  = new Map(districts.map(d => [Number(d.districtCode), d]));

// subdistrict อาจต้องเข้าถึงทั้งตามรหัสและไปรษณีย์
const subdistrictByCode = new Map(subdistricts.map(s => [Number(s.subdistrictCode), s]));

// รวมตำบลทั้งหมดตามรหัสไปรษณีย์ (บางรหัสไปรษณีย์มีหลายตำบล)
const subdistrictsByPostal = subdistricts.reduce((acc, s) => {
  const key = String(s.postalCode);
  (acc[key] ||= []).push(s);
  return acc;
}, {});

// ฟังก์ชันหลัก: ป้อนโค้ดเท่าที่มี จะพยายามเดาช่องว่างจากอีกค่าหนึ่งให้
export function resolveAddress({
  provinceCode,
  districtCode,
  subdistrictCode,
  postalCode,
} = {}) {
  // 1) หา subdistrict ก่อน (ละเอียดสุด)
  let sub = null;
  if (subdistrictCode) {
    sub = subdistrictByCode.get(Number(subdistrictCode)) || null;
  } else if (postalCode) {
    const list = subdistrictsByPostal[String(postalCode)] || [];
    // กรองด้วย districtCode ถ้ามีจะได้แม่นขึ้น
    sub = (districtCode ? list.find(s => Number(s.districtCode) === Number(districtCode)) : list[0]) || null;
  }

  // 2) หากเจอ sub แล้วสามารถอ้าง district/province จาก sub ได้เลย
  const resolvedDistrictCode = Number(
    sub?.districtCode ?? districtCode ?? NaN
  );
  const resolvedProvinceCode = Number(
    sub?.provinceCode
      ?? districtByCode.get(resolvedDistrictCode)?.provinceCode
      ?? provinceCode
      ?? NaN
  );

  const dist = districtByCode.get(resolvedDistrictCode) || null;
  const prov = provinceByCode.get(resolvedProvinceCode) || null;

  return {
    // province
    provinceCode: prov ? Number(prov.provinceCode) : null,
    provinceTh: prov?.provinceNameTh || null,
    provinceEn: prov?.provinceNameEn || null,

    // district
    districtCode: dist ? Number(dist.districtCode) : null,
    districtTh: dist?.districtNameTh || null,
    districtEn: dist?.districtNameEn || null,

    // subdistrict
    subdistrictCode: sub ? Number(sub.subdistrictCode) : null,
    subdistrictTh: sub?.subdistrictNameTh || null,
    subdistrictEn: sub?.subdistrictNameEn || null,

    // postal (ถ้าไม่ได้ส่งมา จะเติมจาก subdistrict)
    postalCode: sub?.postalCode ?? (dist?.postalCode ?? postalCode ?? null),
  };
}

/*
การใช้งานใน Vue 3
------------------
import { resolveAddress } from '@/utils/addressResolver';

const result1 = resolveAddress({
  provinceCode: 10,         // กรุงเทพฯ
  districtCode: 1004,       // บางรัก
  subdistrictCode: 100402,  // สีลม
});
// result1.subdistrictTh === "สีลม", subdistrictEn === "Si Lom", postalCode === 10500 ฯลฯ

const result2 = resolveAddress({ postalCode: 10200 });
// จะคืนค่าตำบลแรก ๆ ที่ตรงรหัสไปรษณีย์ 10200 (หรือเจาะจงด้วย districtCode จะตรงยิ่งขึ้น)
*/
